import { observer } from "mobx-react";
import { useState } from "react";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import IconPlus from "../../../../../components/icons/icon-plus/icon-plus";
import IconWarning from "../../../../../components/icons/icon-warning/icon-warning";
import { BUTTON_VARIANTS } from "../../../../../components/inputs/button-variants";
import { Button } from "../../../../../components/inputs/button/button";
import Card from "../../../../../components/layouts/card/card";
import GridLineLayout from "../../../../../components/layouts/grid-line-layout/grid-line-layout";
import { ConfirmModal } from "../../../../../components/modal/components/confirm-modal/confirm-modal";
import { Note, NOTE_TYPE } from "../../../../../components/note/note";
import { Tooltip } from "../../../../../components/tooltip/tooltip";
import { FEATURES } from "../../../../../models/Plan";
import { RESOLUTION_CATEGORY } from "../../../../../models/ResolutionTypes";
import portalStore from "../../../../../stores/current-portal-store";
import useTranslation from "../../../../../translation/hooks/translation-hook";
import { RESOLUTION_TYPE } from "../../../constants";
import resolutionListStore, { ResolutionsCategoryStore } from "../../../stores/resolution-list-store";
import ResolutionStore from "../../../stores/resolution-store";
import ResolutionLine from "./resolution-line/resolution-line";
import dashboardSettingsStore from "../../../../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES } from "../../../../../models/ProfileCapabilities";

interface ResolutionCardProps {
    title: string,
    description: React.ReactNode,
    toolbarContent: React.ReactNode,
    resolutionCategory: ResolutionsCategoryStore,
    onAddResolutionClicked: (resolutionCategory: RESOLUTION_CATEGORY) => void,
    onExpandClick: (expanded: boolean) => void,
    isExpanded: boolean;
}

const ResolutionsCard: React.FC<ResolutionCardProps> = observer(({ title, description, toolbarContent, onAddResolutionClicked, resolutionCategory, onExpandClick, isExpanded}) => {
    const [resolutionToDelete, setResolutionToDelete] = useState(undefined as ResolutionStore | undefined);
    const { 
        setDeletedKeys, save: saveTranslations
    } = useTranslation();

    const getReturnScoreSettingsState = portalStore.setting?.getReturnScoreSettingsState();

    const addButtonDisabled = (): boolean => {
        if (resolutionCategory.category === RESOLUTION_CATEGORY.exchange_resolutions) {
            return !!resolutionCategory.list.find(resolution => resolution.resolutionType === RESOLUTION_TYPE.ManualExchangeType);
        }

        return false;
    }

    const showResolutionsList = (): boolean => {
        switch (resolutionCategory.category) {
            case RESOLUTION_CATEGORY.returngo_resolutions:
	            const instantCreditsResolutionCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.INSTANT_CREDIT_RESOLUTION);
	            return instantCreditsResolutionCapability.isSupported && instantCreditsResolutionCapability.isFeatureEnabled;
            case RESOLUTION_CATEGORY.cancel_order_resolutions:
				const cancelOrderCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.CANCEL_ORDER);
                return cancelOrderCapability.isSupported && cancelOrderCapability.isFeatureEnabled
            default: 
                return true;
        }
    }

    const showAddResolutionButton = (): boolean => {
        return [
            RESOLUTION_CATEGORY.exchange_resolutions,
            RESOLUTION_CATEGORY.standard_resolutions
        ].includes(resolutionCategory.category)
    }

    const showCancelOrderTooltip = (): boolean => {
        switch (resolutionCategory.category) {
            case RESOLUTION_CATEGORY.cancel_order_resolutions:
	            const cancelOrderCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.CANCEL_ORDER);
	            return cancelOrderCapability.isSupported && !cancelOrderCapability.isFeatureEnabled
            default:
                return false;
        }
    }

    const onResolutionDelete = async (resolution: ResolutionStore) => {
        await resolution.delete();

        resolution.setDeleted(setDeletedKeys);
        saveTranslations();
        resolutionListStore.deleteResolution(resolution);
    }

    const shouldShowReturnScoreSettingsWarning = (): boolean => {
        if (resolutionCategory.category !== RESOLUTION_CATEGORY.returngo_resolutions) {
            return false;
        }

        return !!getReturnScoreSettingsState && getReturnScoreSettingsState?.total !== getReturnScoreSettingsState?.filled;
    }

    return <Card
        toolbar={{
            title: <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{title}</FontBody1>,
            content: toolbarContent
        }}
        isExpandable={true}
        isExpanded={isExpanded}
        expandCallback={onExpandClick}
    >
        <p className="card-instructions">
            <FontBody1 color={FONT_COLOR.PRIMARY3_50}>{description}</FontBody1>
        </p>

        {shouldShowReturnScoreSettingsWarning() &&
            <Note
                type={NOTE_TYPE.WARNING}
                IconComponent={IconWarning}
                caption={`Warning`}
                body={<>
                    Complete the ReturnScore Settings to enable instant credit resolutions
                    <div className="filled-settings-count">
                        <FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY3_50}>
                            {getReturnScoreSettingsState?.filled}
                        </FontBody2>
                        <FontBody2 color={FONT_COLOR.PRIMARY3_50}>/</FontBody2>
                        <FontBody2 color={FONT_COLOR.PRIMARY3_50}>
                            {getReturnScoreSettingsState?.total} Steps are Completed
                        </FontBody2>
                    </div>                    
                </>}
            />
        }

        {showResolutionsList() &&
            <>
                <GridLineLayout>
                    {resolutionCategory.filteredList.map(resolution =>
                        <ResolutionLine 
                            key={resolution.id}
                            category={resolutionCategory.category}
                            resolution={resolution}
                            deleteCallback={setResolutionToDelete}
                            disabled={shouldShowReturnScoreSettingsWarning()}
                        />
                    )}
                </GridLineLayout>

                {showAddResolutionButton() &&
                    <div className="add-resolution-button">
                        <Button
                            variant={BUTTON_VARIANTS.Text}
                            text="add resolution"
                            leftIcon={IconPlus}
                            disabled={addButtonDisabled()}
                            onClick={() => onAddResolutionClicked(resolutionCategory.category)}
                        />
                    </div>
                }
            </>
        }

        {resolutionToDelete &&
            <ConfirmModal
                title="Delete resolution"
                text={
                    <>
                        You asked to <strong>delete</strong> the
                        resolution <strong>{resolutionToDelete.internalName}</strong>
                    </>
                }
                implicationText="Once deleted the resolution and all its translations can’t be restored."
                questionText="Are you sure you want to delete this resolution?"
                cancelButton={{
                    title: 'Cancel',
                    onClick: () => {
                        setResolutionToDelete(undefined);
                    }
                }}
                okButton={{
                    title: 'Delete',
                    onClick: () => {
                        onResolutionDelete(resolutionToDelete);
                        setResolutionToDelete(undefined);
                    }
                }}
            />
        }

        {showCancelOrderTooltip() &&
            <div className="add-resolution-button">
                <Tooltip content={
                    <FontBody1 color={FONT_COLOR.NEUTRAL_WHITE}>
                        This feature is not included in<br />
                        your subscription plan
                    </FontBody1>
                }>
                    <Button
                        variant={BUTTON_VARIANTS.Text}
                        text="add resolution"
                        leftIcon={IconPlus}
                        disabled={true}
                        onClick={() => { }}
                    />
                </Tooltip>
            </div>
        }
    </Card>
});

export default ResolutionsCard;
